import React, { useState, useEffect, useRef, Suspense } from 'react';
import lazyWithReload from './utils/lazyWithReload';
import AppErrorBoundary from './components/AppErrorBoundary';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { authService } from './services/authService';
import {
  HANDOVER_POLICY,
  classifyHandoverError,
  describeSlowHandover,
  runHandover,
} from './services/iosAuthHandover';
import { reportHostIssueOnNextPage } from './utils/hostIssues/hostIssueReporter';
import IOSHandoverError from './components/IOSHandoverError';
import { userService } from './services/userService';
import { recordActivityHeartbeat } from './utils/activityHeartbeat';
import NotFound from './components/NotFound';
import Welcome from './components/Welcome';
import Login from './components/Login';
import SignUp from './components/SignUp';
import ForgotPassword from './components/ForgotPassword';
import PrivateRoute from './components/PrivateRoute';
import AccountRoute from './features/designSandbox/AccountRoute';
import PreviewCode from './components/PreviewCode';
import Waitlist from './components/Waitlist';
import './App.css';
import textureCache from './services/textureCache';
// UserHome, through the route that sends a contractor to the desk
import HomeRoute from './components/HomeRoute';
import UserHome from './components/UserHome';
// ProjectDetail, through the route that opens a contractor's project as a job
import ProjectJobRoute from './books/pages/ProjectJobRoute';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { FullscreenProvider } from './contexts/FullscreenContext';
import { AdminProvider } from './contexts/AdminContext';
import { followSignIn } from './books/adminView';
import { ChatProvider, useChat } from './contexts/ChatContext';
import SessionLinkLauncher from './features/sessionLink/SessionLinkLauncher';
import NotificationBubble from './components/NotificationBubble';
import AdminGate from './components/AdminGate';

// Route-level code splitting.
//
// Every route used to be a static import, so the whole app shipped in one
// chunk: the projects list could not paint until the browser had parsed
// every /debug page, every /admin screen, the bathroom-v2 testers, the 3D
// viewer's three.js stack, web-ifc's WASM wrapper and the react-pdf/jspdf
// stack. Only the auth screens and the post-login landing pages (UserHome,
// ProjectDetail) stay eager — everything a signed-in user sees first.
//
// LoadingScreen is deliberately NOT lazy: it is the Suspense fallback.
import LoadingScreen from './components/LoadingScreen';
import { booksEnabled } from './books/flags';

// `/emulator-signin`: signs a test browser in as a seeded emulator user with
// no password typed (emulatorSignIn/emulatorSignIn.js). Both conditions are
// build-time constants and the whole route — page import and path — sits
// inside the branch they guard, so a production build drops it while
// parsing; `scripts/check-no-emulator-sign-in.js` fails a build that does
// not. Keep it that way: don't hoist the path or the import out of here.
const emulatorSignInRoute =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_USE_EMULATORS === 'true'
    ? (() => {
        const EmulatorSignInPage = lazyWithReload(
          () => import('./emulatorSignIn/EmulatorSignInPage')
        );
        return (
          <Route path="/emulator-signin" element={<EmulatorSignInPage />} />
        );
      })()
    : null;

const StructureDetail = lazyWithReload(
  () => import('./components/StructureDetail')
);
const ContractorPreview = lazyWithReload(
  () => import('./components/contractorPreview/ContractorPreview')
);
const RequestAccess = lazyWithReload(
  () => import('./components/contractorPreview/screens/RequestAccess')
);
const PreviewSupport = lazyWithReload(
  () => import('./components/contractorPreview/screens/PreviewSupport')
);
const AddGLBFile = lazyWithReload(() => import('./components/AddGLBFile'));
const TextureUploader = lazyWithReload(
  () => import('./components/TextureUploader')
);
const GLBGallery = lazyWithReload(() => import('./components/GLBGallery'));
const TubSurroundDebug = lazyWithReload(
  () => import('./components/debug/TubSurroundDebug')
);
const UVFloorPlanPage = lazyWithReload(
  () => import('./components/UVFloorPlanPage')
);
const ElevationsViewerPage = lazyWithReload(
  () => import('./components/ElevationsViewerPage')
);
const CDEditor = lazyWithReload(() => import('./components/CDEditor'));
const DocumentsHub = lazyWithReload(
  () => import('./components/documents/DocumentsHub')
);
const PriceBookRoute = lazyWithReload(
  () => import('./books/pages/priceBook/PriceBookRoute')
);
const BooksHomePage = lazyWithReload(() => import('./books/pages/HomePage'));
const BooksJobsPage = lazyWithReload(() => import('./books/pages/JobsPage'));
const BooksEstimatePage = lazyWithReload(
  () => import('./books/pages/EstimatePage')
);
const BooksEstimatePreviewPage = lazyWithReload(
  () => import('./books/pages/EstimatePreviewPage')
);
const BooksJobPage = lazyWithReload(() => import('./books/pages/JobPage'));
const BooksInvoicesPage = lazyWithReload(
  () => import('./books/pages/InvoicesPage')
);
const BooksInvoicePage = lazyWithReload(
  () => import('./books/pages/InvoicePage')
);
const BooksChangeOrderComposerPage = lazyWithReload(
  () => import('./books/pages/ChangeOrderComposerPage')
);
const BooksChangeOrderEditPage = lazyWithReload(() =>
  import('./books/pages/ChangeOrderComposerPage').then((m) => ({
    default: m.ChangeOrderEditPage,
  }))
);
const BooksChangeOrderPage = lazyWithReload(
  () => import('./books/pages/ChangeOrderPage')
);
const BooksMoneyOutPage = lazyWithReload(
  () => import('./books/pages/MoneyOutPage')
);
const BooksReportsPage = lazyWithReload(
  () => import('./books/pages/ReportsPage')
);
const BooksTermsPage = lazyWithReload(() => import('./books/pages/TermsPage'));
const BooksSettingsPage = lazyWithReload(
  () => import('./books/pages/SettingsPage')
);
const BooksCompanyPage = lazyWithReload(
  () => import('./books/pages/CompanyPage')
);
const BooksJoinPage = lazyWithReload(() => import('./books/pages/JoinPage'));
const BooksAdminViewPage = lazyWithReload(
  () => import('./books/pages/AdminViewPage')
);
const VendorsRoute = lazyWithReload(
  () => import('./books/pages/vendors/VendorsRoute')
);
const CreatorRouter = lazyWithReload(
  () => import('./components/documents/CreatorRouter')
);
const AdminLayout = lazyWithReload(
  () => import('./components/admin/AdminLayout')
);
const AdminDashboard = lazyWithReload(
  () => import('./components/admin/AdminDashboard')
);
const VersionManagement = lazyWithReload(
  () => import('./components/admin/VersionManagement')
);
const UserAnalytics = lazyWithReload(
  () => import('./components/admin/UserAnalytics')
);
const AdminProjectDetails = lazyWithReload(
  () => import('./components/admin/AdminProjectDetails')
);
const UserProjects = lazyWithReload(
  () => import('./components/admin/UserProjects')
);
const ProjectCopier = lazyWithReload(
  () => import('./components/admin/ProjectCopier')
);
const StorageNavigator = lazyWithReload(
  () => import('./components/admin/StorageNavigator')
);
const CrossEnvFiles = lazyWithReload(
  () => import('./components/admin/CrossEnvFiles')
);
const UnifiedAssetCopier = lazyWithReload(
  () => import('./components/admin/UnifiedAssetCopier')
);
const PresetCopier = lazyWithReload(
  () => import('./components/admin/PresetCopier')
);
const UserList = lazyWithReload(() => import('./components/admin/UserList'));
const AdminPriceListNotes = lazyWithReload(
  () => import('./components/admin/AdminPriceListNotes')
);
const AdminOrganizations = lazyWithReload(
  () => import('./components/admin/AdminOrganizations')
);
const AdminOrganization = lazyWithReload(
  () => import('./components/admin/AdminOrganization')
);
const AdminWaitlist = lazyWithReload(
  () => import('./components/admin/AdminWaitlist')
);
const ContactList = lazyWithReload(
  () => import('./components/admin/crm/ContactList')
);
const ContactDetail = lazyWithReload(
  () => import('./components/admin/crm/ContactDetail')
);
const SyncStatus = lazyWithReload(
  () => import('./components/admin/SyncStatus')
);
const PreviewCodeManager = lazyWithReload(
  () => import('./components/admin/PreviewCodeManager')
);
const KohlerCatalog = lazyWithReload(
  () => import('./components/admin/KohlerCatalog')
);
const CorpusPage = lazyWithReload(
  () => import('./components/admin/corpus/CorpusPage')
);
const KitchenRules = lazyWithReload(
  () => import('./components/admin/KitchenRules')
);
const TourScriptPage = lazyWithReload(
  () => import('./components/admin/tourScript/TourScriptPage')
);
const ThumbnailGenerator = lazyWithReload(
  () => import('./components/admin/ThumbnailGenerator')
);
const ToolsDashboard = lazyWithReload(
  () => import('./components/admin/ToolsDashboard')
);
const FeatureFlags = lazyWithReload(
  () => import('./components/admin/FeatureFlags')
);
const SwapFixtureDebug = lazyWithReload(
  () => import('./components/admin/SwapFixtureDebug')
);
const FixCollisionsDebug = lazyWithReload(
  () => import('./components/admin/FixCollisionsDebug')
);
const StageRoomDebug = lazyWithReload(
  () => import('./components/admin/StageRoomDebug')
);
const ApplyStyleDebug = lazyWithReload(
  () => import('./components/admin/ApplyStyleDebug')
);
const LayoutComparison = lazyWithReload(
  () => import('./components/admin/LayoutComparison')
);
const CuratedDesigns = lazyWithReload(
  () => import('./components/admin/CuratedDesigns')
);
const FixtureFields = lazyWithReload(
  () => import('./components/admin/FixtureFields')
);
const FoundingContractors = lazyWithReload(
  () => import('./components/admin/FoundingContractors')
);
const ObjectPanelMocks = lazyWithReload(
  () => import('./components/admin/ObjectPanelMocks')
);
const ContractorAlbums = lazyWithReload(
  () => import('./components/admin/ContractorAlbums')
);
const ProceduralGallery = lazyWithReload(
  () => import('./pages/admin/ProceduralGallery')
);
const StructureCopier = lazyWithReload(
  () => import('./components/admin/StructureCopier')
);
const NightlyTests = lazyWithReload(
  () => import('./components/admin/NightlyTests')
);
const ObjectGallery = lazyWithReload(
  () => import('./components/ObjectGallery')
);
const AlbumAdminRoot = lazyWithReload(
  () => import('./components/album-admin/AlbumAdminRoot')
);
const ProjectList = lazyWithReload(
  () => import('./components/album-admin/ProjectList')
);
const ProjectEditor = lazyWithReload(
  () => import('./components/album-admin/ProjectEditor')
);
const AlbumPreview = lazyWithReload(
  () => import('./components/album-admin/AlbumPreview')
);
const Profile = lazyWithReload(() => import('./components/Profile'));
const IconGallery = lazyWithReload(() => import('./components/IconGallery'));
const TestProjections = lazyWithReload(
  () => import('./components/TestProjections')
);
const FixtureBrowser = lazyWithReload(
  () => import('./components/FixtureBrowser')
);
const DesignPackagesPage = lazyWithReload(
  () => import('./components/DesignPackages/DesignPackagesPage')
);
const FloorplanExtractionPage = lazyWithReload(
  () => import('./components/FloorplanExtractionPage')
);
const ScanRegistrationPage = lazyWithReload(
  () => import('./components/ScanRegistrationPage')
);
const VanityDebugPage = lazyWithReload(
  () => import('./components/VanityDebugPage')
);
const CeilingDebugPage = lazyWithReload(
  () => import('./components/CeilingDebugPage')
);
const PendantShadeDebugPage = lazyWithReload(
  () => import('./components/debug/PendantShadeDebugPage')
);
const TowelHooksDebugPage = lazyWithReload(
  () => import('./components/debug/TowelHooksDebugPage')
);
const MirrorReflectionDebugPage = lazyWithReload(
  () => import('./components/debug/MirrorReflectionDebugPage')
);
const JunctionSlideDebugPage = lazyWithReload(
  () => import('./components/debug/JunctionSlideDebugPage')
);
const ShadeStatesDebugPage = lazyWithReload(
  () => import('./components/debug/ShadeStatesDebugPage')
);
const LightRegionsGalleryPage = lazyWithReload(
  () => import('./components/debug/LightRegionsGalleryPage')
);
const LightComparePage = lazyWithReload(
  () => import('./components/debug/LightComparePage')
);
const ScanRecordingsDebugPage = lazyWithReload(
  () => import('./components/ScanRecordingsDebugPage')
);
const LidarViewerDebugPage = lazyWithReload(
  () => import('./components/debug/LidarViewerDebugPage')
);
const ExistingConditionsDebugPage = lazyWithReload(
  () => import('./components/debug/ExistingConditionsDebugPage')
);
const PlumbingRelocationDebugPage = lazyWithReload(
  () => import('./components/debug/PlumbingRelocationDebugPage')
);
const RoomRecordingViewer = lazyWithReload(
  () => import('./components/sharedRecordings/RoomRecordingViewer')
);
const ShortCodeRedirect = lazyWithReload(
  () => import('./components/sharedRecordings/ShortCodeRedirect')
);
const ProposalPage = lazyWithReload(
  () => import('./features/proposal/public/ProposalPage')
);
const ProposalWalkthrough = lazyWithReload(
  () => import('./features/proposal/public/ProposalWalkthrough')
);
const InspectionReportPage = lazyWithReload(
  () => import('./features/inspection-report/public/InspectionReportPage')
);
const InspectionReviewPage = lazyWithReload(
  () => import('./features/inspection-report/review/InspectionReviewPage')
);
const HomeownerViewer = lazyWithReload(
  () => import('./features/homeowner/HomeownerViewer')
);
const DesignPackagePage = lazyWithReload(
  () => import('./features/designSandbox/public/DesignPackagePage')
);
// The customer's estimate (`/e/:token`): public, the share token is the identity
const EstimatePortalPage = lazyWithReload(
  () => import('./books/portal/EstimatePortalPage')
);
const EstimateWalkthrough = lazyWithReload(
  () => import('./books/portal/EstimateWalkthrough')
);
const DesignPackageWalkthrough = lazyWithReload(
  () => import('./features/designSandbox/public/DesignPackageWalkthrough')
);
const MyDesignsPage = lazyWithReload(
  () => import('./features/designSandbox/MyDesignsPage')
);
const StructureRecordingsPage = lazyWithReload(
  () => import('./components/sharedRecordings/StructureRecordingsPage')
);
const PresetGallery = lazyWithReload(
  () => import('./components/PresetGallery')
);
const TextureGalleryModal = lazyWithReload(
  () => import('./components/TextureGalleryModal')
);
const CeilingAlgorithmDebug = lazyWithReload(
  () => import('./components/CeilingAlgorithmDebug')
);
const TextureRegionDebug = lazyWithReload(
  () => import('./components/debug/TextureRegionDebug')
);
const DormLayoutDebugPage = lazyWithReload(
  () => import('./components/debug/DormLayoutDebugPage')
);
const DormC5Explainer = lazyWithReload(
  () => import('./components/debug/dorm/DormC5Explainer')
);
const DormClearanceExplainer = lazyWithReload(
  () => import('./components/debug/dorm/DormClearanceExplainer')
);
const DormLayoutEditor = lazyWithReload(
  () => import('./components/debug/dorm/DormLayoutEditor')
);
const BathroomTestResultsPage = lazyWithReload(
  () => import('./components/BathroomTestResultsPage')
);
const BathroomReplayPage = lazyWithReload(
  () => import('./components/BathroomReplayPage')
);
const RenderFloorplanPage = lazyWithReload(
  () => import('./components/render/RenderFloorplanPage')
);
const RenderElevationPage = lazyWithReload(
  () => import('./components/render/RenderElevationPage')
);
const SingleWallElevationPage = lazyWithReload(
  () => import('./components/elevation/SingleWallElevationPage')
);

// One shared chunk — these are named exports of a single barrel module.
const Stage1Tester = lazyWithReload(() =>
  import('./components/bathroomAnalyzerV2/__devTools__').then((m) => ({
    default: m.Stage1Tester,
  }))
);
const Stage2Tester = lazyWithReload(() =>
  import('./components/bathroomAnalyzerV2/__devTools__').then((m) => ({
    default: m.Stage2Tester,
  }))
);
const Stage3Tester = lazyWithReload(() =>
  import('./components/bathroomAnalyzerV2/__devTools__').then((m) => ({
    default: m.Stage3Tester,
  }))
);
const Stage4Tester = lazyWithReload(() =>
  import('./components/bathroomAnalyzerV2/__devTools__').then((m) => ({
    default: m.Stage4Tester,
  }))
);
const Stage5Tester = lazyWithReload(() =>
  import('./components/bathroomAnalyzerV2/__devTools__').then((m) => ({
    default: m.Stage5Tester,
  }))
);
const Stage6Tester = lazyWithReload(() =>
  import('./components/bathroomAnalyzerV2/__devTools__').then((m) => ({
    default: m.Stage6Tester,
  }))
);
const ExpansionTester = lazyWithReload(() =>
  import('./components/bathroomAnalyzerV2/__devTools__').then((m) => ({
    default: m.ExpansionTester,
  }))
);
const AllResultsViewer = lazyWithReload(() =>
  import('./components/bathroomAnalyzerV2/__devTools__').then((m) => ({
    default: m.AllResultsViewer,
  }))
);
const CustomStartViewer = lazyWithReload(() =>
  import('./components/bathroomAnalyzerV2/__devTools__').then((m) => ({
    default: m.CustomStartViewer,
  }))
);
const RoomDebugViewer = lazyWithReload(() =>
  import('./components/bathroomAnalyzerV2/__devTools__').then((m) => ({
    default: m.RoomDebugViewer,
  }))
);
const AddShowerViewer = lazyWithReload(() =>
  import('./components/bathroomAnalyzerV2/__devTools__').then((m) => ({
    default: m.AddShowerViewer,
  }))
);
const BathroomListViewer = lazyWithReload(() =>
  import('./components/bathroomAnalyzerV2/__devTools__').then((m) => ({
    default: m.BathroomListViewer,
  }))
);
const CompleteBathroomViewer = lazyWithReload(() =>
  import('./components/bathroomAnalyzerV2/__devTools__').then((m) => ({
    default: m.CompleteBathroomViewer,
  }))
);
// Fire-and-forget: record that an authenticated user opened the app, so
// `lastActiveAt` reflects real usage (including persisted-session opens that
// never re-trigger a sign-in). Throttled per-user inside recordActivityHeartbeat.
function recordUserActivity(userId) {
  if (!userId) return;
  recordActivityHeartbeat(userId, {
    write: (id) => userService.updateLastActive(id),
  });
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  // an admin's read-only view of a books organization is the signed-in
  // admin's: any change of account — a sign-out from any page or tab — ends
  // it (books/adminView.js). Not before sign-in has settled: `user` starts
  // null on every load, and a reload must keep the view.
  useEffect(() => {
    if (!authLoading) followSignIn(user?.uid ?? null);
  }, [authLoading, user]);
  const [notification, setNotification] = useState(null);
  const [isAuthenticatingFromIOS, setIsAuthenticatingFromIOS] = useState(false);
  // 'retryable' | 'rejected' once the iOS sign-in handover has given up.
  const [iosAuthFailure, setIOSAuthFailure] = useState(null);
  const { showLoading, hideLoading } = useLoading();
  const location = useLocation();
  const { conversationContext, updateContext } = useChat();

  // Handle browser navigation (back/forward) to clear caches when switching projects
  useEffect(() => {
    const handlePopState = () => {
      const currentPath = window.location.pathname;
      const projectMatch = currentPath.match(/\/project\/([^/]+)/);
      const newProjectId = projectMatch ? projectMatch[1] : null;

      // If we've navigated to a different project, clear previous project's cached data
      if (
        newProjectId !== conversationContext?.location?.projectId &&
        conversationContext?.location?.projectId
      ) {
        updateContext({
          cache: {
            ...conversationContext.cache,
            structures: [],
            rooms: [],
            checkpoints: [],
          },
        });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [
    conversationContext?.location?.projectId,
    conversationContext?.cache,
    updateContext,
  ]);

  useEffect(() => {
    // Check for authentication token in URL (from iOS app)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const uid = urlParams.get('uid');
    const source = urlParams.get('source');

    // Capture the original path before any redirects
    const originalPath = window.location.pathname;

    if (token && uid && source === 'ios') {
      const authStartTime = performance.now();
      console.log('[iOS Auth] Starting authentication flow for uid:', uid);

      // Set flag to prevent login page from showing
      setIsAuthenticatingFromIOS(true);

      // Wait for Firebase Auth to finish initializing (restoring session
      // from IndexedDB). With shared WKProcessPool the session persists,
      // so onAuthStateChanged will fire with the existing user quickly.
      const waitForAuthInit = () => {
        return new Promise((resolve) => {
          // Check synchronously first
          const immediateUser = authService.getCurrentUser();
          if (immediateUser) {
            console.log(
              '[iOS Auth] Immediate getCurrentUser:',
              immediateUser.uid,
              `(${Math.round(performance.now() - authStartTime)}ms)`
            );
            resolve(immediateUser);
            return;
          }

          // Wait for Firebase to restore session from IndexedDB
          let initTimeout;
          const unsubscribe = authService.onAuthStateChanged((restoredUser) => {
            clearTimeout(initTimeout);
            unsubscribe();
            console.log(
              '[iOS Auth] onAuthStateChanged fired:',
              restoredUser?.uid || 'none',
              `(${Math.round(performance.now() - authStartTime)}ms)`
            );
            resolve(restoredUser);
          });

          // Safety timeout — don't wait forever. Cleared when auth answers
          // first; left running, it logged a timeout that never happened
          // into the history every issue report carries.
          initTimeout = setTimeout(() => {
            unsubscribe();
            console.log(
              '[iOS Auth] Auth init timeout, proceeding',
              `(${Math.round(performance.now() - authStartTime)}ms)`
            );
            resolve(null);
          }, 2000);
        });
      };

      waitForAuthInit().then((currentUser) => {
        if (currentUser && currentUser.uid === uid) {
          console.log(
            '[iOS Auth] Already authenticated, skipping token verification',
            `(${Math.round(performance.now() - authStartTime)}ms)`
          );
          setUser(currentUser);
          setAuthLoading(false);
          recordUserActivity(currentUser.uid);
          const url = new URL(window.location);
          url.searchParams.delete('token');
          url.searchParams.delete('uid');
          window.history.replaceState({}, '', url.toString());
          setIsAuthenticatingFromIOS(false);
          hideLoading();
          return;
        }

        // No existing session — trade the app's token for one. A failure
        // ends on IOSHandoverError, never the web sign-in: signing in is the
        // app's job. See services/iosAuthHandover.js.
        console.log(
          '[iOS Auth] Need to authenticate via Cloud Function',
          `(${Math.round(performance.now() - authStartTime)}ms)`
        );
        showLoading('Authenticating...');
        const handoverStartTime = performance.now();
        const retryErrors = [];

        // Store the intended destination in sessionStorage before any redirects
        const intendedDestination =
          originalPath !== '/login'
            ? originalPath
            : sessionStorage.getItem('iosIntendedPath') || '/';
        sessionStorage.setItem('iosIntendedPath', intendedDestination);

        // A long first attempt shouldn't look frozen.
        const slowNotice = setTimeout(
          () => showLoading('Still connecting…'),
          HANDOVER_POLICY.slowNoticeMs
        );

        // Authenticate with the token from iOS app
        runHandover(() => authService.verifyAndSignInWithToken(token, uid), {
          onRetry: (attempt, error) => {
            retryErrors.push(error);
            console.warn(
              `[iOS Auth] Handover failed (${error.name}: ${error.message}); attempt ${attempt} of ${HANDOVER_POLICY.attempts}`,
              `(${Math.round(performance.now() - authStartTime)}ms)`
            );
            showLoading('Still connecting…');
          },
        })
          .then((authenticatedUser) => {
            clearTimeout(slowNotice);

            // A slow success leaves no error behind, so say so — on the
            // next page, since the redirect below could cancel a request.
            const slowHandover = describeSlowHandover({
              elapsedMs: performance.now() - handoverStartTime,
              attempts: retryErrors.length + 1,
              retryErrors,
            });
            if (slowHandover) {
              reportHostIssueOnNextPage('slow-ios-handover', slowHandover);
            }

            // Update user state immediately
            setUser(authenticatedUser);
            setAuthLoading(false);

            // Get the intended destination from sessionStorage
            const intendedDestination =
              sessionStorage.getItem('iosIntendedPath') || '/';

            // Clean up URL parameters (keep source and other params for UI conditionals)
            const url = new URL(window.location);
            url.searchParams.delete('token');
            url.searchParams.delete('uid');
            // Keep source=ios and other params like navigationMode

            // Clear the stored path
            sessionStorage.removeItem('iosIntendedPath');

            // Build redirect URL preserving remaining query params
            const remainingParams = url.searchParams.toString();
            const redirectURL = remainingParams
              ? `${intendedDestination}?${remainingParams}`
              : intendedDestination;

            setIsAuthenticatingFromIOS(false);
            hideLoading();
            window.location.replace(redirectURL);
          })
          .catch((error) => {
            clearTimeout(slowNotice);
            const failure = classifyHandoverError(error);
            console.error(
              `Failed to authenticate with iOS token (${failure}):`,
              error
            );
            setIsAuthenticatingFromIOS(false);
            hideLoading();
            setIOSAuthFailure(failure);
          });
      });
      // Return early to prevent setting up the auth listener twice
      return;
    }

    // Only set up regular auth flow if not from iOS
    showLoading();

    // Set up auth listener
    const unsubscribe = authService.onAuthStateChanged((user) => {
      // A share-link visitor is signed in ANONYMOUSLY
      // (`authService.ensureShareSession`); to the rest of the app that is
      // nobody — no private routes, no activity written to a `users` doc.
      const accountUser = user && !user.isAnonymous ? user : null;
      setUser(accountUser);
      setAuthLoading(false);
      hideLoading();
      recordUserActivity(accountUser?.uid);
    });

    return unsubscribe;
  }, []); // Empty deps - this should only run once on mount

  useEffect(() => {
    // Cleanup texture cache on app unmount
    return () => {
      textureCache.clearCache();
    };
  }, []);

  // Ahead of `authLoading`, which a failed handover leaves true: sign-in never
  // finished, so nothing else may render — and an empty page is what users saw.
  if (iosAuthFailure) {
    return <IOSHandoverError kind={iosAuthFailure} />;
  }

  if (authLoading) {
    return null; // LoadingProvider will show the loading screen
  }

  return (
    <div className="App">
      {notification && (
        <NotificationBubble
          {...notification}
          onClose={() => setNotification(null)}
        />
      )}
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/r/:token/:roomUuid" element={<RoomRecordingViewer />} />
          {/* Homeowner view-only sharing (public, no PrivateRoute) */}
          <Route path="/h/:token" element={<HomeownerViewer />} />
          <Route path="/h/:token/room/:roomId" element={<HomeownerViewer />} />
          {/* Customer proposal, online version (public, no PrivateRoute) */}
          <Route path="/p/:token" element={<ProposalPage />} />
          {/* Inspection report, frozen copy (public, no PrivateRoute) */}
          <Route path="/i/:token" element={<InspectionReportPage />} />
          <Route path="/p/:token/walk" element={<ProposalWalkthrough />} />
          <Route
            path="/p/:token/walk/:roomId"
            element={<ProposalWalkthrough />}
          />
          {/* A homeowner's saved design package (public, no PrivateRoute) —
              the link they send their contractor and their friends. */}
          <Route path="/e/:token" element={<EstimatePortalPage />} />
          <Route path="/e/:token/walk" element={<EstimateWalkthrough />} />
          <Route
            path="/e/:token/walk/:roomId"
            element={<EstimateWalkthrough />}
          />
          <Route path="/d/:token" element={<DesignPackagePage />} />
          <Route path="/d/:token/walk" element={<DesignPackageWalkthrough />} />
          <Route path="/s/:code" element={<ShortCodeRedirect />} />
          <Route path="/login" element={<Welcome />} />
          <Route path="/signin" element={<Login />} />
          {emulatorSignInRoute}
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/preview-code" element={<PreviewCode />} />
          <Route path="/preview" element={<ContractorPreview />} />
          <Route path="/request-access" element={<RequestAccess />} />
          <Route path="/preview-support" element={<PreviewSupport />} />
          <Route path="/waitlist" element={<Waitlist />} />
          <Route
            path="/"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <HomeRoute />
              </PrivateRoute>
            }
          />
          {/* the classic projects list, always (a contractor's `/` is the
              desk; this keeps rename, delete and floor-plan upload) */}
          <Route
            path="/projects"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <UserHome />
              </PrivateRoute>
            }
          />
          <Route
            path="/price-book"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <PriceBookRoute />
              </PrivateRoute>
            }
          />
          <Route
            path="/vendors"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <VendorsRoute />
              </PrivateRoute>
            }
          />
          {booksEnabled() && (
            <>
              {/* An invite link (phase 3d §3): outside PrivateRoute and the
                  desk — a new invitee has no access yet, and joining is
                  what gives it; the page handles being signed out. */}
              <Route
                path="/books/join/:token"
                element={<BooksJoinPage user={user} />}
              />
              <Route
                path="/books"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksHomePage />
                  </PrivateRoute>
                }
              />
              {/* An admin's read-only view of an organization's desk
                  (phase 3d §7): opened only for the admin claim, through
                  the callable that logs it; anyone else lands on /books. */}
              <Route
                path="/books/view/:orgId"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksAdminViewPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/books/jobs"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksJobsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/books/invoices"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksInvoicesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/books/jobs/:id/change-orders/new"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksChangeOrderComposerPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/books/change-orders/:id/edit"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksChangeOrderEditPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/books/change-orders/:id"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksChangeOrderPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/books/invoices/:id"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksInvoicePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/books/terms"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksTermsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/books/settings"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksSettingsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/books/settings/company"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksCompanyPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/books/money-out"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksMoneyOutPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/books/reports"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksReportsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/books/jobs/:id"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksJobPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/books/estimates/:id"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksEstimatePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/books/estimates/:id/preview"
                element={
                  <PrivateRoute
                    user={user}
                    isAuthenticatingFromIOS={isAuthenticatingFromIOS}
                  >
                    <BooksEstimatePreviewPage />
                  </PrivateRoute>
                }
              />
            </>
          )}
          <Route
            path="/project/:id"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                {/* a contractor's project opens as the job (phase 3b) */}
                <ProjectJobRoute />
              </PrivateRoute>
            }
          />
          <Route
            path="/structure/:id"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <StructureDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/structure/:id/recordings"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <StructureRecordingsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/structure/:id/inspection"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <InspectionReviewPage />
              </PrivateRoute>
            }
          />
          {/* Catalog tools moved under /admin (admin-only). Keep the old
            URLs working for bookmarks. */}
          <Route
            path="/addglbfile"
            element={<Navigate to="/admin/add-glb" replace />}
          />
          <Route
            path="/addtexture"
            element={<Navigate to="/admin/add-texture" replace />}
          />
          <Route
            path="/glbgallery"
            element={<Navigate to="/admin/glb-gallery" replace />}
          />
          <Route
            path="/tub-surround-debug"
            element={
              <PrivateRoute user={user}>
                <TubSurroundDebug />
              </PrivateRoute>
            }
          />
          <Route
            path="/pretransformed-floorplan/:structureId"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <UVFloorPlanPage onNotification={setNotification} />
              </PrivateRoute>
            }
          />
          <Route
            path="/scan-registration/:structureId"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <ScanRegistrationPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/elevations/:structureId"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <ElevationsViewerPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/project/:projectId/cd-editor/:docIndex"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <CDEditor />
              </PrivateRoute>
            }
          />
          <Route
            path="/project/:projectId/documents"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <DocumentsHub />
              </PrivateRoute>
            }
          />
          <Route
            path="/project/:projectId/documents/:docType/create"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <CreatorRouter />
              </PrivateRoute>
            }
          />
          <Route
            path="/floorplan-schematic-gallery"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <ObjectGallery />
              </PrivateRoute>
            }
          />
          <Route
            path="/icon-gallery"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <IconGallery />
              </PrivateRoute>
            }
          />
          <Route
            path="/test-projections"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <TestProjections />
              </PrivateRoute>
            }
          />
          <Route path="/fixtures" element={<FixtureBrowser />} />
          {/* A homeowner's own area. Gated on HAVING AN ACCOUNT, not on the
              contractor entitlements PrivateRoute checks — see AccountRoute. */}
          <Route
            path="/my-designs"
            element={
              <AccountRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <MyDesignsPage />
              </AccountRoute>
            }
          />
          <Route
            path="/design-packages"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <DesignPackagesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/floorplan-extraction"
            element={<FloorplanExtractionPage />}
          />
          {/* The Fixture Gallery was the GLB Gallery filtered to
            source === 'fixtures'; that is a Source control there now, so this
            alias points at the gallery that absorbed it. */}
          <Route
            path="/fixturegallery"
            element={<Navigate to="/admin/glb-gallery" replace />}
          />
          <Route
            path="/debug/dorm"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <AdminGate>
                  <DormLayoutDebugPage />
                </AdminGate>
              </PrivateRoute>
            }
          />
          <Route
            path="/debug/dorm-c5"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <AdminGate>
                  <DormC5Explainer />
                </AdminGate>
              </PrivateRoute>
            }
          />
          <Route
            path="/debug/dorm-clearance"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <AdminGate>
                  <DormClearanceExplainer />
                </AdminGate>
              </PrivateRoute>
            }
          />
          <Route
            path="/debug/dorm-editor"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <AdminGate>
                  <DormLayoutEditor />
                </AdminGate>
              </PrivateRoute>
            }
          />
          <Route path="/debug/vanity" element={<VanityDebugPage />} />
          <Route path="/debug/vanity/:caseId" element={<VanityDebugPage />} />
          <Route path="/debug/ceiling" element={<CeilingDebugPage />} />
          <Route path="/debug/ceiling/:name" element={<CeilingDebugPage />} />
          <Route
            path="/debug/pendant-shade"
            element={<PendantShadeDebugPage />}
          />
          <Route
            path="/debug/mirror-reflection"
            element={<MirrorReflectionDebugPage />}
          />
          <Route
            path="/debug/junction-slide"
            element={<JunctionSlideDebugPage />}
          />
          <Route path="/debug/towel-hooks" element={<TowelHooksDebugPage />} />
          <Route
            path="/debug/shade-states"
            element={<ShadeStatesDebugPage />}
          />
          <Route
            path="/debug/shade-states/:modelId"
            element={<ShadeStatesDebugPage />}
          />
          <Route
            path="/debug/light-regions"
            element={<LightRegionsGalleryPage />}
          />
          <Route path="/debug/light-compare" element={<LightComparePage />} />
          <Route
            path="/debug/texture-regions"
            element={
              <PrivateRoute user={user}>
                <TextureRegionDebug />
              </PrivateRoute>
            }
          />
          <Route
            path="/debug/texture-regions/:structureId"
            element={
              <PrivateRoute user={user}>
                <TextureRegionDebug />
              </PrivateRoute>
            }
          />
          <Route
            path="/debug/scan-recordings/:structureId"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <ScanRecordingsDebugPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/debug/lidar-viewer/:structureId"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <AdminGate>
                  <LidarViewerDebugPage />
                </AdminGate>
              </PrivateRoute>
            }
          />
          <Route
            path="/debug/existing-conditions/:structureId"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <AdminGate>
                  <ExistingConditionsDebugPage />
                </AdminGate>
              </PrivateRoute>
            }
          />
          <Route
            path="/debug/plumbing-relocation/:structureId"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <AdminGate>
                  <PlumbingRelocationDebugPage />
                </AdminGate>
              </PrivateRoute>
            }
          />
          <Route
            path="/debug/plumbing-relocation"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <AdminGate>
                  <PlumbingRelocationDebugPage />
                </AdminGate>
              </PrivateRoute>
            }
          />
          <Route
            path="/presets"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                {/* Admin-only: its Style Fixtures tab writes the product
                    catalog, which only admins may. */}
                <AdminGate>
                  <PresetGallery />
                </AdminGate>
              </PrivateRoute>
            }
          />
          {/* Legacy top-level alias — see /fixturegallery above. */}
          <Route
            path="/texturegallery"
            element={<Navigate to="/admin/texture-gallery" replace />}
          />
          <Route path="/bathroom-v2/stage1" element={<Stage1Tester />} />
          <Route path="/bathroom-v2/stage2" element={<Stage2Tester />} />
          <Route path="/bathroom-v2/stage3" element={<Stage3Tester />} />
          <Route path="/bathroom-v2/stage4" element={<Stage4Tester />} />
          <Route path="/bathroom-v2/stage5" element={<Stage5Tester />} />
          <Route path="/bathroom-v2/stage6" element={<Stage6Tester />} />
          <Route path="/bathroom-v2/expansion" element={<ExpansionTester />} />
          <Route path="/bathroom-v2/results" element={<AllResultsViewer />} />
          <Route
            path="/bathroom-v2/customStart"
            element={<CustomStartViewer />}
          />
          <Route path="/bathroom-v2/room" element={<RoomDebugViewer />} />
          <Route path="/bathroom-v2/addShower" element={<AddShowerViewer />} />
          <Route path="/bathroom-v2/list" element={<BathroomListViewer />} />
          <Route
            path="/bathroom-v2/completeBathroom"
            element={<CompleteBathroomViewer />}
          />
          <Route
            path="/bathroom-test-results"
            element={
              <PrivateRoute user={user}>
                <BathroomTestResultsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/debug/bathroom-replay"
            element={
              <PrivateRoute user={user}>
                <BathroomReplayPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/ceiling-debug"
            element={
              <PrivateRoute user={user}>
                <CeilingAlgorithmDebug />
              </PrivateRoute>
            }
          />
          {/* Render pages for iOS hidden WebView screenshot capture */}
          {/* These handle their own auth via token in URL params - no PrivateRoute needed */}
          <Route
            path="/render/floorplan/:structureId/:roomId"
            element={<RenderFloorplanPage />}
          />
          {/* Structure-wide floorplan (no roomId) - shows all rooms */}
          <Route
            path="/render/floorplan/:structureId"
            element={<RenderFloorplanPage />}
          />
          <Route
            path="/render/elevation/:structureId/:roomId/:wallIndex"
            element={<RenderElevationPage />}
          />
          {/* Interactive single-wall elevation viewer for iOS WKWebView */}
          <Route
            path="/elevation/:structureId/:roomId/:wallIndex"
            element={<SingleWallElevationPage />}
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute
                user={user}
                isAuthenticatingFromIOS={isAuthenticatingFromIOS}
              >
                <AdminLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<UserAnalytics />} />
            <Route path="users/:userId/projects" element={<UserProjects />} />
            {/* Menu entry point: no user in the URL yet, so the page shows a
              picker and then navigates to the addressable route below. */}
            <Route path="copy-projects" element={<ProjectCopier />} />
            <Route
              path="users/:userId/copy-projects"
              element={<ProjectCopier />}
            />
            <Route
              path="projects/:projectId"
              element={<AdminProjectDetails />}
            />
            <Route path="storage" element={<StorageNavigator />} />
            <Route path="versions" element={<VersionManagement />} />
            <Route path="cross-env" element={<CrossEnvFiles />} />
            <Route path="unifiedAssets" element={<UnifiedAssetCopier />} />
            <Route path="presets" element={<PresetCopier />} />
            <Route path="user-list" element={<UserList />} />
            <Route path="organizations" element={<AdminOrganizations />} />
            <Route path="price-list-notes" element={<AdminPriceListNotes />} />
            <Route path="organizations/:id" element={<AdminOrganization />} />
            <Route path="waitlist" element={<AdminWaitlist />} />
            <Route path="crm" element={<ContactList />} />
            <Route path="crm/:contactId" element={<ContactDetail />} />
            <Route path="sync-status" element={<SyncStatus />} />
            <Route path="preview-codes" element={<PreviewCodeManager />} />
            <Route path="kohler-catalog" element={<KohlerCatalog />} />
            <Route path="kitchen-rules" element={<KitchenRules />} />
            <Route path="tour-script" element={<TourScriptPage />} />
            <Route path="tools" element={<ToolsDashboard />} />
            <Route path="feature-flags" element={<FeatureFlags />} />
            <Route path="curated-designs" element={<CuratedDesigns />} />
            <Route path="thumbnails" element={<ThumbnailGenerator />} />
            <Route
              path="founding-contractors"
              element={<FoundingContractors />}
            />
            <Route path="contractor-albums" element={<ContractorAlbums />} />
            <Route path="procedural-gallery" element={<ProceduralGallery />} />
            <Route path="copy-structure" element={<StructureCopier />} />
            <Route path="glb-gallery" element={<GLBGallery />} />
            <Route path="texture-gallery" element={<TextureGalleryModal />} />
            <Route path="add-glb" element={<AddGLBFile />} />
            <Route path="add-texture" element={<TextureUploader />} />
            {/* Development harnesses. Grouped under /admin/debug/ so the
              operations nav is not interleaved with them; the old
              /admin/<name> paths redirect here so bookmarks survive. */}
            <Route path="debug/fixture-fields" element={<FixtureFields />} />
            <Route path="debug/swap-fixture" element={<SwapFixtureDebug />} />
            <Route
              path="debug/fix-collisions"
              element={<FixCollisionsDebug />}
            />
            <Route path="debug/apply-style" element={<ApplyStyleDebug />} />
            <Route path="debug/stage-room" element={<StageRoomDebug />} />
            <Route
              path="debug/layout-comparison"
              element={<LayoutComparison />}
            />
            <Route
              path="debug/object-panel-mocks"
              element={<ObjectPanelMocks />}
            />
            <Route path="debug/nightly-tests" element={<NightlyTests />} />
            <Route path="corpus" element={<CorpusPage />} />
            {/* The builder is now the corpus page's "Add scans" tab. */}
            <Route
              path="debug/emulator-corpus"
              element={<Navigate to="/admin/corpus?tab=add" replace />}
            />
            <Route
              path="fixture-fields"
              element={<Navigate to="/admin/debug/fixture-fields" replace />}
            />
            <Route
              path="swap-fixture-debug"
              element={<Navigate to="/admin/debug/swap-fixture" replace />}
            />
            <Route
              path="fix-collisions-debug"
              element={<Navigate to="/admin/debug/fix-collisions" replace />}
            />
            <Route
              path="apply-style-debug"
              element={<Navigate to="/admin/debug/apply-style" replace />}
            />
            <Route
              path="layout-comparison"
              element={<Navigate to="/admin/debug/layout-comparison" replace />}
            />
            <Route
              path="object-panel-mocks"
              element={
                <Navigate to="/admin/debug/object-panel-mocks" replace />
              }
            />
            <Route
              path="nightly-tests"
              element={<Navigate to="/admin/debug/nightly-tests" replace />}
            />
            <Route
              path="emulator-corpus"
              element={<Navigate to="/admin/corpus?tab=add" replace />}
            />
          </Route>
          <Route path="/profile" element={<Profile />} />
          <Route path="/album-admin" element={<AlbumAdminRoot user={user} />} />
          <Route
            path="/album-admin/:slug"
            element={
              <AlbumAdminRoot user={user}>
                <ProjectList />
              </AlbumAdminRoot>
            }
          />
          <Route
            path="/album-admin/:slug/preview"
            element={
              <AlbumAdminRoot user={user}>
                <AlbumPreview />
              </AlbumAdminRoot>
            }
          />
          <Route
            path="/album-admin/:slug/preview/:projectId"
            element={
              <AlbumAdminRoot user={user}>
                <AlbumPreview />
              </AlbumAdminRoot>
            }
          />
          <Route
            path="/album-admin/:slug/:projectId"
            element={
              <AlbumAdminRoot user={user}>
                <ProjectEditor />
              </AlbumAdminRoot>
            }
          />
          <Route path="/not-found" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </div>
  );
}

// A page that throws — or whose file a deploy took away — says so instead
// of unmounting the app to a blank olive screen; moving to another page
// (the path) tries again.
function PageErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <AppErrorBoundary resetKey={location.pathname}>{children}</AppErrorBoundary>
  );
}

function App() {
  return (
    <BrowserRouter>
      <PageErrorBoundary>
        <LoadingProvider>
          <AdminProvider>
            <ChatProvider>
              <FullscreenProvider>
                <AppContent />
              </FullscreenProvider>
              {/* Dev only: a live Claude Code session for this worktree */}
              <SessionLinkLauncher />
            </ChatProvider>
          </AdminProvider>
        </LoadingProvider>
      </PageErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
